import React, { useEffect, useMemo, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { KTSVG } from '../../../../_metronic/helpers'
import { AuthenticateUser } from '../../../Utils/_helpers/RoleAndPermission'
import './WalletCardTemplates.scss'
import moment from 'moment-timezone'
import NodataYet from '../../../Components/NodataYet'
import DeleteConfirmationPopup from '../../../Components/DeleteConfirmationPopup'
import { useTimezone } from '../../../hooks/useTimezone'
import ReactPaginate from 'react-paginate'
import {
  STATUS,
  VALUE_INACTIVE,
  VALUE_CARD,
  VALUE_CARD_UPPER,
  TAB_TEMPLATE,
  TOTAL_SCAN,
  pageSizeOptions,
} from './constants'
import {CardItem} from './types'
import {useFetchWalletCardTemplates} from './hooks/useFetchWalletCardTemplates'
import {useI18n} from '../../../Utils/_helpers/useI18n'
import {MenuComponent} from '../../../../_metronic/assets/ts/components'
import {WalletCardTemplateMobileCard} from './components/WalletCardTemplateMobileCard'
import { formatDateWithUserTimezone } from './utils'

const WalletCardTemplates = () => {
  const navigate = useNavigate()
  const { t } = useI18n()
  const { userTimezone } = useTimezone()
  const [open, setOpen] = useState(false)
  const [deleteIds, setDeleteIds] = useState<string | null>(null)
  const [isMobileView, setIsMobileView] = useState(false)

  const {
    templates,
    page,
    setPage,
    error,
    isLoading,
    fetchData,
    retryFetchData,
    handleDelete: deleteTemplate,
    handleSetDefault: setDefaultTemplate,
    handlePageChange,
  } = useFetchWalletCardTemplates()

  const handleDelete = (data: CardItem) => {
    setDeleteIds(data.id)
    setOpen(true)
  }

  const handleConfirmDelete = async () => {
    if (!deleteIds) return
    await deleteTemplate(deleteIds, () => {
      setOpen(false)
      setDeleteIds(null)
    })
  }

  useEffect(() => {
    fetchData()
  }, [page, fetchData])

  // Detect screen size for mobile view
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobileView(window.innerWidth <= 768)
    }

    checkScreenSize()
    window.addEventListener('resize', checkScreenSize)
    return () => window.removeEventListener('resize', checkScreenSize)
  }, [])

  // Add this new useEffect to reinitialize the menu when component mounts or is updated
  useEffect(() => {
    // Wait for DOM to be fully rendered
    const timer = setTimeout(() => {
      // Reinitialize all menus in the component
      MenuComponent.createInstances('[data-kt-menu="true"]')

      // Force update dropdown menus
      MenuComponent.updateDropdowns()
    }, 200)

    return () => {
      clearTimeout(timer)
    }
  }, [templates.items]) // Reinitialize when the list items change

  // Add this new useEffect
  useEffect(() => {
    // Force Metronic menu initialization
    setTimeout(() => {
      MenuComponent.reinitialization()

      // Look for any wallet-card specific menus and initialize them
      const dropdownMenus = document.querySelectorAll('.menu-action-wallet-card')
      if (dropdownMenus.length > 0) {
        dropdownMenus.forEach((menu) => {
          if (menu instanceof HTMLElement) {
            menu.setAttribute('data-kt-menu', 'true')
          }
        })

        // Reinitialize again to catch newly marked elements
        MenuComponent.reinitialization()
      }
    }, 300)
  }, [templates.items]) // Reinitialize when the items change

  // Pagination handler comes from custom hook

  const handleEdit = (data: CardItem) => {
    navigate(`/card-templates/edit/${data.id}`, {state: {isEdit: true, isTemplate: true}})
  }

  const handleDetail = (data: CardItem) => {
    navigate(`/card-templates/detail/${data.id}`)
  }

  const handleSetDefault = (data: CardItem) => {
    setDefaultTemplate(data)
  }

  const handleCreateNew = () => {
    navigate('/card-templates/create', {state: {isEdit: false, isTemplate: true}})
  }

  const renderActions = (data: any, idx: number, total: number) => {
    const commonStyle = {verticalAlign: 'center'}
    const isNearBottom = idx >= total - 2

    return (
      <td key={`${data.id}-action`} className='ms-2 text-end' style={commonStyle}>
        <div className='dropdown-hover-show'>
          <button
            className='btn btn-sm btn-icon btn-bg-light btn-active-light-primary d-flex align-items-center justify-content-center'
            data-kt-menu-trigger='hover'
            data-kt-menu-placement={isNearBottom ? 'top-end' : 'bottom-end'}
            data-kt-menu-overflow='false'
          >
            <span className='more-action-icon'>...</span>
          </button>

          <div
            className='menu menu-sub menu-sub-dropdown menu-column menu-rounded menu-state-bg-light-primary fw-bold fs-7 w-200px py-4'
            data-kt-menu='true'
          >
            <div className='menu-item px-3' onClick={() => handleEdit(data)}>
              <span className='menu-link px-3 d-flex align-items-center'>
                <span className='svg-icon svg-icon-2 me-3'>
                  <i className='bi bi-pencil me-2'></i>
                </span>
                <span>{t('COMMON.ACTIONS.EDIT')}</span>
              </span>
            </div>

            <div className='menu-item px-3' onClick={() => handleDetail(data)}>
              <span className='menu-link px-3 d-flex align-items-center'>
                <span className='svg-icon svg-icon-2 me-3'>
                  <i className='bi bi-eye me-2'></i>
                </span>
                <span>{t('COMMON.ACTIONS.DETAIL')}</span>
              </span>
            </div>

            {!data.isDefault && (
              <div
                className={`menu-item px-3 ${data.status !== STATUS.ACTIVE ? 'disabled' : ''}`}
                onClick={data.status === STATUS.ACTIVE ? () => handleSetDefault(data) : undefined}
              >
                <span
                  className={`menu-link px-3 d-flex align-items-center ${
                    data.status !== STATUS.ACTIVE ? 'text-muted' : ''
                  }`}
                >
                  <span className='svg-icon svg-icon-2 me-3'>
                    <i className='bi bi-star me-2'></i>
                  </span>
                  <span>Set Default</span>
                </span>
              </div>
            )}

            <div className='menu-item px-3' onClick={() => handleDelete(data)}>
              <span className='menu-link px-3 d-flex align-items-center text-danger'>
                <span className='svg-icon svg-icon-danger svg-icon-2 me-3'>
                  <i className='bi bi-trash me-2 text-danger'></i>
                </span>
                <span>{t('COMMON.ACTIONS.DELETE')}</span>
              </span>
            </div>
          </div>
        </div>
      </td>
    )
  }

  const renderCell = useCallback(
    (key: string, data: CardItem, idx: number) => {
      const commonStyle = {verticalAlign: 'middle'}

      switch (key) {
        case 'imagePreviewUrl':
          return (
            <td key={`${data.id}-${key}`} style={commonStyle} className='w-100px'>
              <img src={data.imagePreviewUrl} alt='Preview' className='img-preview w-80px' />
            </td>
          )
        case 'type':
          return (
            <td key={`${data.id}-${key}`} className='ps-3 icon-business-card'>
              <KTSVG
                className='svg-icon-business-card'
                path='/media/icons/wallet-card/business-card.svg'
              />
            </td>
          )
        case 'cardType':
          return (
            <td key={`${data.id}-${key}`} style={commonStyle}>
              <span className={`text-dark d-block mb-1 fs-6 card-type ${data.type}`}>
                {data.type
                  .replace(/([A-Z])/g, ' $1')
                  .replace(/^./, (str) => str.toUpperCase())
                  .trim()
                  .replace(VALUE_CARD, VALUE_CARD_UPPER)}
              </span>
            </td>
          )
        case 'cardName':
          return (
            <td key={`${data.id}-${key}`} style={commonStyle}>
              <div>
                <div className='d-flex align-items-center'>
                  {data.isDefault && (
                    <i
                      className='bi bi-star-fill me-2'
                      style={{color: '#ffc107', fontSize: '16px'}}
                    ></i>
                  )}
                  <span
                    className='text-dark d-block mb-1 fs-6 wallet-card-name cursor-pointer'
                    onClick={() => handleDetail(data)}
                  >
                    {data.cardName}
                  </span>
                </div>
              </div>
            </td>
          )
        case 'createdAt':
          return (
            <td key={`${data.id}-${key}`} style={commonStyle}>
              <span className='text-dark fw-400 d-block mb-1 fs-6 created-at'>
                {formatDateWithUserTimezone(data?.createdAt || '', 'MMMM DD, YYYY hh:mm A')}
              </span>
            </td>
          )
        case 'updatedAt':
          return (
            <td key={`${data.id}-${key}`} style={commonStyle}>
              <span className='text-dark fw-400 d-block mb-1 fs-6 updated-at'>
                {formatDateWithUserTimezone(data?.updatedAt || '', 'MMMM DD, YYYY hh:mm A')}
              </span>
            </td>
          )
        case 'status':
          return (
            <td key={`${data.id}-${key}`} style={commonStyle}>
              <span
                className={`badge ${data.status === STATUS.ACTIVE ? 'bg-success' : 'bg-danger'}`}
              >
                {data.status === STATUS.INACTIVE
                  ? VALUE_INACTIVE
                  : data.status?.charAt(0).toUpperCase() + data.status?.slice(1)}
              </span>
            </td>
          )
        case 'action':
          return renderActions(data, idx, templates.items.length)
        default:
          return (
            <td
              key={`${data.id}-${key}`}
              style={commonStyle}
              className={`${key === TOTAL_SCAN ? 'text-center' : ''}`}
            >
              {data[key] || '-'}
            </td>
          )
      }
    },
    [templates.items.length, formatDateWithUserTimezone, handleDetail, renderActions]
  )

  const renderTable = useMemo(() => {
    if (!templates.items || templates.items.length === 0) {
      return <NodataYet message={t('MOBILE_WALLET_CARD.NO_DATA_WALLET_CARD_TEMPLATE_LIST')} />
    }

    if (isMobileView) {
      return (
        <>
          <div className='mobile-cards-container'>
            {templates.items.map((data) => {
              return (
                <WalletCardTemplateMobileCard
                  key={data.id}
                  card={data}
                  onEdit={handleEdit}
                  onView={handleDetail}
                  onDelete={handleDelete}
                  onSetDefault={handleSetDefault}
                />
              )
            })}
          </div>
          <div className='mobile-pagination'>
            <select
              value={page?.size}
              className='SelectSize'
              onChange={(e: any) => {
                setPage({...page, size: parseInt(e.target.value), page: 1})
              }}
            >
              {pageSizeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
              {templates?.total > 0 &&
                templates?.total > pageSizeOptions[pageSizeOptions.length - 1].value && (
                  <option value={templates?.total}>All</option>
                )}
            </select>
            <ReactPaginate
              previousLabel='<'
              nextLabel='>'
              pageCount={templates?.totalPages}
              forcePage={templates.current - 1}
              onPageChange={(e) => handlePageChange(e.selected)}
              containerClassName={'pagination'}
              previousLinkClassName={'pagination__link'}
              nextLinkClassName={'pagination__link'}
              disabledClassName={'pagination__link--disabled'}
              activeClassName={'pagination__link--active'}
            />
          </div>
        </>
      )
    }

    // Desktop view with table
    return (
      <>
        <div className='table-responsive'>
          <table className='table table-row-dashed table-row-gray-200 align-middle gs-0 gy-4'>
            <thead>
              <tr className='fw-400 text-muted'>
                {TAB_TEMPLATE.map((tab: {key: string; label: string}) => (
                  <th
                    key={tab.key}
                    className={tab.key === 'action' ? 'min-w-150px text-end' : 'min-w-100px'}
                  >
                    {tab.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {templates.items.map((data, idx) => (
                <tr key={data.id}>{TAB_TEMPLATE.map((tab) => renderCell(tab.key, data, idx))}</tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className='abcd'>
          <select
            value={page?.size}
            className='SelectSize'
            onChange={(e: any) => {
              setPage({...page, size: parseInt(e.target.value), page: 1})
            }}
          >
            {pageSizeOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
            {templates?.total > 0 &&
              templates?.total > pageSizeOptions[pageSizeOptions.length - 1].value && (
                <option value={templates?.total}>All</option>
              )}
          </select>
          <ReactPaginate
            previousLabel='<'
            nextLabel='>'
            pageCount={templates?.totalPages}
            forcePage={templates.current - 1}
            onPageChange={(e) => handlePageChange(e.selected)}
            containerClassName={'pagiantion'}
            previousLinkClassName={'pagination__link'}
            nextLinkClassName={'pagination__link'}
            disabledClassName={'pagination__link--disabled'}
            activeClassName={'pagination__link--active'}
          />
        </div>
      </>
    )
  }, [
    templates,
    page,
    renderCell,
    handlePageChange,
    isMobileView,
    handleDetail,
    handleEdit,
    handleSetDefault,
    setPage,
    t,
  ])

  return (
    <>
      <div className='card wallet-card-list'>
        <div className='card-header border-0 pt-12 px-14'>
          <h3 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bolder fs-2'>
              {t('MOBILE_WALLET_CARD.LIST.TEMPLATE.TITLE')}
            </span>
            <span className='text-muted fw-bold fs-7'>{t('COMMON.ORDERED_BY_MOST_RECENT')}</span>
          </h3>
          {AuthenticateUser('create-template') && (
            <div className='card-toolbar'>
              <div className='d-flex card-toolbar'>
                <button className='btn btn-primary p-4' onClick={handleCreateNew}>
                  {t('MOBILE_WALLET_CARD.LIST.TEMPLATE.BUTTON.CREATE')}
                </button>
              </div>
            </div>
          )}
        </div>

        <div className={`card-body py-3 ${isMobileView ? 'px-4' : 'px-14'}`}>{renderTable}</div>
      </div>
      <DeleteConfirmationPopup
        show={open}
        handleClose={() => {
          setOpen(false)
          setDeleteIds(null)
        }}
        confirm={handleConfirmDelete}
        Message={t('COMMON.ACTIONS.DELETE_CONFIRMATION', {name: 'wallet card'})}
      />
    </>
  )
}

export default WalletCardTemplates
